/**
 * PATH: packages/ui/src/components/email/CategoryPill.tsx
 * PURPOSE:
 *   - Pure presentational pill rendering an email's current
 *     category — icon + label + optional lock + optional
 *     dropdown caret. NO API calls, NO toast, NO data fetching.
 *
 * ROLE IN ARCHITECTURE:
 *   - Lift-and-shift target for ass-dashboard's local
 *     `CategoryChip.tsx` button-rendering JSX. The host's
 *     `CategoryChip` (which still owns the dropdown menu, the
 *     PATCH call, and the toast) becomes a thin wrapper that
 *     composes this primitive plus its hooks.
 *
 * WHY SO BARE
 *   - Three different host apps render this thing in three
 *     subtly-different surfaces (inbox row, detail header,
 *     filter bar). Pinning behaviour into the component would
 *     force every host to inherit ass-dashboard's exact UX.
 *     Keep it dumb; let the host wire it up.
 *
 * NOTES FOR FUTURE AI:
 *   - The lucide icon is passed as a *prop* (`icon: LucideIcon`)
 *     so this file pins a peer-dep on lucide-react but never
 *     imports a specific glyph. Hosts pick which glyph fits
 *     their theme.
 *   - Use `cn` from the package's local lib/utils — DO NOT
 *     reach into a host's `@/lib/utils` alias.
 */

import type { LucideIcon } from 'lucide-react'
import { ChevronDown, Lock } from 'lucide-react'

import { cn } from '../../lib/utils'

export interface CategoryPillProps {
  /** Tailwind classes for the pill background + text colour
   *  (e.g. "bg-orange-100 text-orange-700"). Host owns the
   *  category-key → palette mapping so each tenant can theme
   *  freely. */
  pillClass: string
  /** Lucide icon component the host picks for this category
   *  (e.g. `Receipt` for "bill", `FileText` for "quote"). */
  icon: LucideIcon
  /** Display label, e.g. "Supplier Bill". */
  label: string
  /** True when the category has been pinned by a user — surfaces a
   *  small lock icon so the row visually advertises that the AI
   *  classifier won't override it. */
  locked?: boolean
  /** Compact variant — smaller padding/font, used in the email
   *  list. "md" is for detail surfaces. */
  size?: 'sm' | 'md'
  /** When provided, render a solid colour dot (hex) as the leading
   *  marker instead of the lucide glyph — the Monday/ClickUp "tag"
   *  look. Additive + opt-in: every existing caller that omits it
   *  keeps the icon-led pill unchanged. */
  dotColor?: string
  /** When provided, the pill renders as a button and exposes a
   *  caret indicating "click to change". When omitted the pill
   *  is purely informational and is keyboard-inert. */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** Tooltip override; defaults derived from `label` + `locked`. */
  title?: string
  /** Disabled state (host wires this from its async pending
   *  flag). */
  disabled?: boolean
}

export function CategoryPill({
  pillClass,
  icon: Icon,
  label,
  locked,
  size = 'sm',
  onClick,
  title,
  disabled,
  dotColor,
}: CategoryPillProps) {
  // sm = the inbox-row chip: aligned to the uniform CHIP_BASE grammar
  // (px-2 py-0.5 text-[10px]) so it sits the same height/weight as the work,
  // mailbox + suggestion chips beside it (operator 2026-06-22: chips "all over").
  // Was text-[9px] px-1.5 — the sub-10px "hard to read" floor + a tighter pad
  // that made it the odd one out in the strip. md keeps its detail-surface size.
  const sizeCls =
    size === 'sm'
      ? 'px-2 py-0.5 text-[10px] gap-1'
      : 'px-2 py-0.5 text-[10px] gap-1'
  const iconCls = size === 'sm' ? 'h-3 w-3' : 'h-3 w-3'
  const dotCls = size === 'sm' ? 'h-2 w-2' : 'h-2 w-2'

  const computedTitle =
    title ??
    (locked
      ? `Pinned as ${label} — click to change`
      : onClick
      ? `Click to override (${label})`
      : label)

  const inner = (
    <>
      {dotColor ? (
        <span
          className={cn('rounded-full shrink-0', dotCls)}
          style={{ backgroundColor: dotColor }}
          aria-hidden
        />
      ) : (
        <Icon className={cn(iconCls, 'shrink-0')} />
      )}
      {/* Single-line label — never wrap to a second row ("Supplier / Bill")
          inside a width-constrained chip lane (BUG-2026-06-21-CATEGORY-PILL-
          WRAP). `truncate` caps a pathologically long label with an ellipsis;
          `min-w-0` lets it shrink under flex pressure rather than forcing wrap. */}
      <span className="min-w-0 max-w-[140px] truncate whitespace-nowrap">{label}</span>
      {locked ? <Lock className={cn(iconCls, 'ml-0.5 shrink-0')} /> : null}
      {onClick ? (
        <ChevronDown className={cn(iconCls, 'ml-0.5 shrink-0 opacity-60')} />
      ) : null}
    </>
  )

  const baseCls = cn(
    'rounded-full font-medium inline-flex items-center transition-shadow max-w-full',
    sizeCls,
    pillClass,
  )

  if (onClick) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        title={computedTitle}
        className={cn(
          baseCls,
          'hover:ring-1 hover:ring-foreground/20 disabled:opacity-50',
        )}
      >
        {inner}
      </button>
    )
  }

  return (
    <span title={computedTitle} className={baseCls}>
      {inner}
    </span>
  )
}
